import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

interface CommonDatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  onClose: () => void;
  minDate?: string | null;
  maxDate?: string | null;
  anchorEl?: HTMLElement | null;
  saveAsDateOnly?: boolean;
}

const CommonDatePicker = ({
  value,
  onChange,
  onClose,
  minDate,
  maxDate,
  anchorEl,
  saveAsDateOnly = false,
}: CommonDatePickerProps) => {
  const [_view, setView] = useState<'year' | 'month' | 'day'>('day');
  const nextYear = new Date().getFullYear() + 1;
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const resolvedMin = minDate ? dayjs(minDate) : dayjs(new Date(lastYear, 0, 1));
  const resolvedMax = maxDate ? dayjs(maxDate) : dayjs(new Date(nextYear, 11, 31));
  const safeMax = resolvedMax.isBefore(resolvedMin, "day") ? resolvedMin : resolvedMax;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        open={Boolean(anchorEl)}
        onClose={onClose}
        value={value ? dayjs(value) : null}
        format="MMM DD, YYYY"
        // Fires when user finishes selecting a date (after year/month/day)
        onAccept={(newValue) => {
          const boundedValue =
            newValue && maxDate && newValue.isAfter(dayjs(maxDate), "day")
              ? dayjs(maxDate)
              : newValue;
          onChange(
            boundedValue
              ? saveAsDateOnly
                ? boundedValue.format("YYYY-MM-DD")
                : boundedValue.toISOString()
              : null
          );
          onClose();
          setView('day'); // reset view for next open
        }}
        openTo="day"
        views={['year', 'month', 'day']}
        onViewChange={(newView) => setView(newView)}
        onOpen={() => setView('day')}
        minDate={resolvedMin}
        maxDate={safeMax}
        components={{ OpenPickerIcon: () => null }}
        slotProps={{
          textField: { sx: { display: 'none' } },
          popper: {
            anchorEl: anchorEl || undefined,
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'offset',
                options: { offset: [0, 18] },
              },
            ],
          },
        }}
        sx={{
          width: 0,
          height: 0,
          '& .MuiPickersPopper-root': {
            boxShadow: 3,
            borderRadius: 1,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CommonDatePicker;
